import React, { useState, useRef, useEffect } from "react";
import {
  FaMicrophone,
  FaPlay,
  FaStop,
  FaTimes,
  FaSpinner,
} from "react-icons/fa";
import clsx from "clsx";

interface AudioPreviewChipProps {
  audioBlob: Blob;
  onRemove: () => void;
  isSentAttachment?: boolean;
  isTranscribing?: boolean;
}

/**
 * Utility function to format duration in human-readable format
 */
const formatDuration = (seconds: number): string => {
  if (!isFinite(seconds) || seconds <= 0) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

/**
 * AudioPreviewChip Component
 *
 * Displays a compact preview of a recorded audio clip before sending the message.
 * Shows a microphone icon, label, duration, playback toggle, and a remove button.
 * Visually and structurally mirrors FilePreviewChip.
 *
 * @param audioBlob - The recorded audio Blob
 * @param onRemove - Callback function when user clicks the remove button
 * @param isSentAttachment - If true, hides the remove button (for sent messages)
 * @param isTranscribing - If true, shows a spinner and "Transcribing..." label
 */
export const AudioPreviewChip: React.FC<AudioPreviewChipProps> = ({
  audioBlob,
  onRemove,
  isSentAttachment = false,
  isTranscribing = false,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState<number>(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const urlRef = useRef<string>("");

  useEffect(() => {
    urlRef.current = URL.createObjectURL(audioBlob);
    const audio = new Audio(urlRef.current);
    audioRef.current = audio;

    audio.onloadedmetadata = () => {
      // Some browsers report Infinity for streaming blobs; handle gracefully
      if (isFinite(audio.duration)) {
        setDuration(audio.duration);
      }
    };

    audio.onended = () => {
      setIsPlaying(false);
    };

    return () => {
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
      }
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [audioBlob]);

  const togglePlay = () => {
    if (!audioRef.current || isTranscribing) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const chipClasses = clsx(
    "file-preview-chip",
    "audio-preview-chip",
    isSentAttachment && "sent-attachment-chip"
  );

  return (
    <div className={chipClasses}>
      {/* Icon: spinner while transcribing, mic icon otherwise */}
      {isTranscribing ? (
        <FaSpinner className="file-preview-icon fa-spin" />
      ) : (
        <FaMicrophone
          className="file-preview-icon"
          style={{ color: "#ef4444" }}
        />
      )}

      {/* Info: label and duration/status */}
      <div className="file-preview-info">
        <div className="file-preview-name" title="Voice recording">
          Voice Message
        </div>
        <div className="file-preview-size">
          {isTranscribing ? "Transcribing..." : formatDuration(duration)}
        </div>
      </div>

      {/* Playback toggle button */}
      {!isSentAttachment && (
        <button
          className="audio-play-btn"
          onClick={togglePlay}
          disabled={isTranscribing}
          aria-label={isPlaying ? "Pause playback" : "Play recording"}
        >
          {isPlaying ? <FaStop /> : <FaPlay />}
        </button>
      )}

      {/* Remove button (hidden for sent attachments, same as FilePreviewChip) */}
      {!isSentAttachment && (
        <button
          className="file-preview-remove"
          onClick={onRemove}
          disabled={isTranscribing}
          aria-label="Remove recording"
        >
          <FaTimes />
        </button>
      )}
    </div>
  );
};